import Phaser from 'phaser';
import { CONFIG } from '../config.js';

const C = CONFIG.COLORS;

export class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y) {
    super(scene, x, y, 'hero');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setCollideWorldBounds(true);
    this.setScale(2.5);
    // 40% display diameter circular hitbox, regardless of texture size.
    const hitR = this.width * 0.2;
    this.body.setCircle(hitR, this.width / 2 - hitR, this.height / 2 - hitR);

    this.maxHp = CONFIG.PLAYER_HP_MAX;
    this.hp = this.maxHp;
    this.baseSpeed = CONFIG.PLAYER_SPEED;
    this.speed = this.baseSpeed;

    this.invulnUntil = 0;
    this.shieldUntil = 0;
    this.speedBoostUntil = 0;
    this.doubleDmgUntil = 0;
    this.alive = true;
    this.xp = 0;

    const auraR = Math.max(22, this.displayWidth * 0.6);
    this.shieldGfx = scene.add
      .circle(x, y, auraR, C.SHIELD, 0)
      .setStrokeStyle(2, C.SHIELD, 0)
      .setDepth(this.depth + 1);

    this.boostGfx = scene.add
      .circle(x, y, auraR - 4, C.UI_GOLD, 0)
      .setStrokeStyle(2, C.UI_GOLD, 0);

    this.dblGfx = scene.add
      .circle(x, y, auraR - 2, C.UI_PRIMARY, 0)
      .setStrokeStyle(2, C.UI_PRIMARY, 0);

    this.facing = { x: 0, y: -1 };
  }

  isInvuln() { return this.scene.time.now < this.invulnUntil; }
  hasShield() { return this.scene.time.now < this.shieldUntil; }
  hasDoubleDmg() { return this.scene.time.now < this.doubleDmgUntil; }
  hasSpeedBoost() { return this.scene.time.now < this.speedBoostUntil; }

  setMoveVector(x, y) {
    const len = Math.hypot(x, y);
    if (len > 0.05) {
      const inv = 1 / len;
      const nx = x * inv;
      const ny = y * inv;
      this.body.setVelocity(nx * this.speed, ny * this.speed);
      this.facing.x = nx;
      this.facing.y = ny;
    } else {
      this.body.setVelocity(0, 0);
    }
  }

  takeDamage(amount, opts = {}) {
    if (!this.alive) return 0;
    if (this.isInvuln() && !opts.ignoreInvuln) return 0;
    if (this.hasShield()) {
      this.shieldUntil = 0;
      this.invulnUntil = this.scene.time.now + 600;
      this.scene.events.emit('shield-broken');
      this.setTintFill(0x60a5fa);
      this.scene.time.delayedCall(120, () => this.clearTint());
      return 0;
    }
    this.hp = Math.max(0, this.hp - amount);
    if (!opts.ignoreInvuln) {
      this.invulnUntil = this.scene.time.now + CONFIG.PLAYER_INVULN_MS;
    }
    this.setTintFill(0xffffff);
    this.scene.time.delayedCall(120, () => this.clearTint());
    if (this.hp <= 0) this.alive = false;
    return amount;
  }

  heal(amount) {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }

  applyKnockback(fromX, fromY, force = CONFIG.PLAYER_KNOCKBACK) {
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const d = Math.hypot(dx, dy) || 1;
    this.body.setVelocity((dx / d) * force, (dy / d) * force);
  }

  applyPickup(type) {
    const now = this.scene.time.now;
    switch (type) {
      case 'heart':
        this.heal(CONFIG.HEART_HP_AMOUNT);
        break;
      case 'gem':
        this.xp += CONFIG.GEM_XP_AMOUNT;
        break;
      case 'speed':
        this.speedBoostUntil = now + CONFIG.POWERUP_SPEED_MS;
        this.speed = this.baseSpeed * CONFIG.POWERUP_SPEED_MULT;
        break;
      case 'shield':
        this.shieldUntil = now + CONFIG.POWERUP_SHIELD_MS;
        break;
      case 'doubleDmg':
        this.doubleDmgUntil = now + CONFIG.POWERUP_DOUBLE_DMG_MS;
        break;
    }
  }

  preUpdate(time, delta) {
    super.preUpdate(time, delta);
    if (!this.hasSpeedBoost()) this.speed = this.baseSpeed;

    // Aura indicators
    const shieldOn = this.hasShield();
    this.shieldGfx.setPosition(this.x, this.y);
    this.shieldGfx.setStrokeStyle(2, C.SHIELD, shieldOn ? 0.85 : 0);
    this.shieldGfx.setFillStyle(C.SHIELD, shieldOn ? 0.18 : 0);

    const boostOn = this.hasSpeedBoost();
    this.boostGfx.setPosition(this.x, this.y + 14);
    this.boostGfx.setStrokeStyle(2, C.UI_GOLD, boostOn ? 0.7 : 0);

    const dblOn = this.hasDoubleDmg();
    this.dblGfx.setPosition(this.x, this.y - 14);
    this.dblGfx.setStrokeStyle(2, C.UI_PRIMARY, dblOn ? 0.7 : 0);

    // Invuln blink
    this.alpha = this.isInvuln() ? (Math.floor(time / 80) % 2 ? 0.6 : 1) : 1;
  }

  destroy(fromScene) {
    if (this.shieldGfx) this.shieldGfx.destroy();
    if (this.boostGfx) this.boostGfx.destroy();
    if (this.dblGfx) this.dblGfx.destroy();
    super.destroy(fromScene);
  }
}
